import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { KartStatus, type KartListItem, resolveMaintenanceRecordsAccess } from '@kangaroo/shared';
import { PageHeader, colors } from '@kangaroo/ui';

import { fetchKarts, kartsListKey } from '../../features/karts';
import { currentTeamEntitlementsKey, currentTeamKey, fetchCurrentTeam, fetchTeamEntitlements } from '../../features/teams';
import {
  filterKartQueue,
  sortKartsByServiceUrgency,
  summarizeFleetHealth,
  type KartQueueFilter,
} from '../../lib/fleetOps';
import { canManageKarts } from '../../lib/roles';
import { viewerIsTeamWorkspaceOwner } from '../../lib/teamWorkspace';
import { PAGE_ICONS } from '../../lib/pageIcons';
import { TableRowsSkeleton } from '../../components/loading/PageSkeletons';
import { useAuthStore } from '../../store/authStore';

const FILTER_OPTIONS: Array<{ key: KartQueueFilter; label: string }> = [
  { key: 'service-due', label: 'Service due' },
  { key: 'maintenance', label: 'In shop' },
  { key: 'active', label: 'Ready' },
  { key: 'all', label: 'All' },
];

function queueBadge(kart: KartListItem) {
  const remaining = kart.maintenance.remainingHours;
  if (remaining != null && remaining <= 0) {
    return 'Service due';
  }

  if (kart.status === KartStatus.MAINTENANCE) {
    return 'In shop';
  }

  if (remaining != null && remaining <= 1) {
    return 'Due soon';
  }

  return kart.status.toLowerCase();
}

export function MaintenanceQueuePage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const role = user?.role;
  const { data: teamProfile } = useSWR(currentTeamKey, fetchCurrentTeam);
  const isWorkspaceOwner = viewerIsTeamWorkspaceOwner(user, teamProfile);
  const canManage = role ? canManageKarts(role, { isTeamWorkspaceOwner: isWorkspaceOwner }) : false;
  const [filter, setFilter] = useState<KartQueueFilter>('service-due');
  const { data: entitlementsSnapshot } = useSWR(currentTeamEntitlementsKey, fetchTeamEntitlements);
  const showMaintenanceFeatures = resolveMaintenanceRecordsAccess({
    entitlements: entitlementsSnapshot?.entitlements,
    isFamilyTeamWorkspace: teamProfile?.isFamilyTeamWorkspace,
  });

  const { data: kartsPage, isLoading } = useSWR(
    showMaintenanceFeatures ? kartsListKey({ perPage: 100 }) : null,
    () => fetchKarts({ perPage: 100 }),
  );

  const karts = useMemo(() => {
    const all = kartsPage?.data ?? [];
    return sortKartsByServiceUrgency(filterKartQueue(all, filter));
  }, [filter, kartsPage?.data]);

  const health = summarizeFleetHealth(kartsPage?.data ?? []);

  if (!showMaintenanceFeatures) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 720, mx: 'auto' }}>
        <PageHeader
          title="Maintenance queue"
          subtitle="Service logging and fleet maintenance workflows."
          icon={PAGE_ICONS.maintenance}
          mb={1}
        />
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Maintenance is included on club plans. Family workspaces can add it from Billing.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/drivers?billing=1')} sx={{ textTransform: 'none' }}>
          Go to billing
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1080, mx: 'auto' }}>
      <PageHeader
        title="Maintenance queue"
        subtitle={
          isLoading
            ? 'Loading vehicle queue…'
            : `${health.serviceDue} due · ${health.inMaintenance} in shop · ${health.active} ready`
        }
        icon={PAGE_ICONS.maintenance}
        mb={2}
        actions={(
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/karts')}>
            All vehicles
          </Button>
        )}
      />

      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        {FILTER_OPTIONS.map((option) => (
          <Button
            key={option.key}
            size="small"
            variant={filter === option.key ? 'contained' : 'outlined'}
            onClick={() => setFilter(option.key)}
          >
            {option.label}
          </Button>
        ))}
      </Stack>

      <TableContainer sx={{ border: '1px solid rgba(36,62,82,0.78)', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vehicle</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Hours left</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRowsSkeleton rows={7} columns={5} />
            ) : karts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No vehicles in this queue.</TableCell>
              </TableRow>
            ) : (
              karts.map((kart) => (
                <TableRow key={kart.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800 }}>#{kart.kartNumber}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                      {kart.nickname ?? kart.chassisBrand ?? 'Kart'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={queueBadge(kart)} sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    {kart.maintenance.remainingHours == null ? 'N/A' : `${kart.maintenance.remainingHours.toFixed(1)}h`}
                  </TableCell>
                  <TableCell>{kart.assignedDriver?.name ?? 'Unassigned'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => navigate(`/karts?vehicle=${kart.id}`)}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!canManage ? (
        <Typography sx={{ mt: 1.5, fontSize: '0.72rem', color: colors.primary[200] }}>
          Read-only queue view. Mechanics and team staff can log service from the vehicle inspector.
        </Typography>
      ) : null}
    </Box>
  );
}
